// src/pages/clientes/components/ClienteFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Input,
} from '@/components/ui';
import { clienteSchema, type ClienteFormData } from '../schemas/clienteSchema';
import { useCrearCliente, useActualizarCliente } from '@/hooks/queries/useClientes';
import type { Cliente } from '@/types';

interface Props {
  open: boolean;
  onClose: () => void;
  cliente?: Cliente | null;
}

export function ClienteFormModal({ open, onClose, cliente }: Props) {
  const isEditing = !!cliente;
  const crearMutation = useCrearCliente();
  const actualizarMutation = useActualizarCliente();

  const form = useForm<ClienteFormData>({
    resolver: zodResolver(clienteSchema),
    defaultValues: {
      codigo: '',
      nombre: '',
      nombre_corto: '',
      modo_operacion: 'SIMPLE',
    },
  });

  // Reset form when cliente changes
  useEffect(() => {
    if (cliente) {
      form.reset({
        codigo: cliente.codigo || '',
        nombre: cliente.nombre,
        nombre_corto: cliente.nombre_corto || '',
        modo_operacion: (cliente.modo_operacion as ClienteFormData['modo_operacion']) || 'SIMPLE',
      });
    } else {
      form.reset({
        codigo: '',
        nombre: '',
        nombre_corto: '',
        modo_operacion: 'SIMPLE',
      });
    }
  }, [cliente, form]);

  const onSubmit = async (data: ClienteFormData) => {
    try {
      // Clean empty optional strings
      const payload = {
        ...data,
        nombre_corto: data.nombre_corto || undefined,
      };
      if (isEditing && cliente) {
        await actualizarMutation.mutateAsync({ id: cliente.id, data: payload });
      } else {
        await crearMutation.mutateAsync(payload);
      }
      handleClose();
    } catch {
      // Error handled by mutation
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  const isLoading = crearMutation.isPending || actualizarMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Código *"
              placeholder="CLI-001"
              {...form.register('codigo')}
              error={form.formState.errors.codigo?.message}
              disabled={isEditing}
            />
            <div>
              <label className="text-sm font-medium mb-1.5 block">Modo Operación</label>
              <select
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                {...form.register('modo_operacion')}
              >
                <option value="SIMPLE">Simple</option>
                <option value="ESTANDAR">Estándar</option>
                <option value="CORPORATIVO">Corporativo</option>
              </select>
            </div>
          </div>

          <Input
            label="Nombre *"
            placeholder="Nombre completo del cliente"
            {...form.register('nombre')}
            error={form.formState.errors.nombre?.message}
          />

          <Input
            label="Nombre Corto"
            placeholder="Nombre abreviado (opcional)"
            {...form.register('nombre_corto')}
            error={form.formState.errors.nombre_corto?.message}
          />

          <DialogFooter className="pt-4">
            <Button type="button" variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={isLoading}>
              {isEditing ? 'Guardar Cambios' : 'Crear Cliente'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
